import {
  PropsWithChildren,
  Children,
  FC,
  useRef,
  ReactNode,
  useMemo,
  useState,
  useLayoutEffect,
} from "react";
import { DateTimeIcon, DownIcon, MoreIcon, TimeIcon } from "@components/icons";
import { Menu, MenuItem, ClickEvent } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import { Button } from "@components/button";
import "./action.scss";

export type ActionVarient = "select" | "date-picker" | "time-picker";

interface ActionProps extends PropsWithChildren {
  className?: string;
  mode?: ActionVarient;
  label?: ReactNode;
  keepOpenOnClick?: boolean;
  handleItemClick?: (event: ClickEvent) => void;
  disabled?: boolean;
  containerClassName?: string;
  zIndex?: string;
  portal?: boolean;
  menuItemClassName?: string;
  textColor?: string;
  drawerMode?: boolean;
}

export const Action: FC<ActionProps> = ({
  className = "",
  children,
  mode,
  label,
  handleItemClick,
  disabled,
  keepOpenOnClick,
  containerClassName,
  zIndex,
  portal,
  menuItemClassName = "",
  textColor = "",
  drawerMode,
}) => {
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuButtonWidth, setMenuButtonWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (menuButtonRef.current) {
      setMenuButtonWidth(menuButtonRef.current.getBoundingClientRect().width);
    }
  }, []);

  const menuStyle = useMemo(
    () => ({
      width: mode === "select" ? menuButtonWidth : "auto",
      zIndex,
    }),
    [mode, menuButtonWidth, zIndex],
  );

  return (
    <Menu
      portal={!!portal}
      onItemClick={(e) => {
        if (keepOpenOnClick) {
          e.stopPropagation = true;
          e.keepOpen = true;
        }
        handleItemClick?.(e);
      }}
      menuClassName={`${containerClassName} ${mode} ${drawerMode ? "drawer" : ""}`}
      menuStyle={menuStyle}
      menuButton={
        <Button
          className={`${className} ${label ? "" : textColor} action-mode ${mode}`}
          ref={menuButtonRef}
          disabled={!!disabled}
        >
          {label ? label : <MoreIcon />}
          {mode && (
            <span className="arrow-down">
              {mode === "select" && <DownIcon size="12" />}
              {mode === "date-picker" && <DateTimeIcon />}
              {mode === "time-picker" && <TimeIcon />}
            </span>
          )}
        </Button>
      }
    >
      {Children.map(children, (child, index) => {
        return (
          <MenuItem key={index} className={menuItemClassName}>
            {child}
          </MenuItem>
        );
      })}
    </Menu>
  );
};
